/**
 * leadDetailAnchors — the stable deep-link contract into LeadDetailPage.
 *
 * Producers (Action Center cards, FollowUpBoard) build hrefs with these
 * helpers; LeadDetailPage resolves them into a section to reveal. Nobody
 * scrolls by pixels — sections carry stable ids and the page uses
 * scrollIntoView on them once the lead has loaded, so a normal click and
 * "open in new tab" behave identically.
 *
 *   /leads/:id#conversation                   → WhatsApp conversation
 *                                               (composer focused)
 *   /leads/:id?compose_template=<id>#compose  → message workflow with the
 *                                               recommended template
 *                                               loaded (never auto-sent)
 */
export const LEAD_FOCUS_CONVERSATION = "conversation";
export const LEAD_FOCUS_COMPOSE = "compose";

export const LEAD_SECTION_CONVERSATION_ID = "section-conversation";
export const LEAD_SECTION_TEMPLATES_ID = "section-templates-anchor";

/** Display heuristic shared with LeadConversation (backend is authoritative). */
export const WHATSAPP_WINDOW_MS = 24 * 60 * 60 * 1000;

export function leadHref(leadId) {
  return `/leads/${encodeURIComponent(leadId)}`;
}

export function leadConversationHref(leadId) {
  return `${leadHref(leadId)}#${LEAD_FOCUS_CONVERSATION}`;
}

export function leadComposeHref(leadId, templateId = null) {
  const query = templateId
    ? `?compose_template=${encodeURIComponent(templateId)}`
    : "";
  return `${leadHref(leadId)}${query}#${LEAD_FOCUS_COMPOSE}`;
}

/**
 * Parse a location into a focus request. A `compose_template` param alone
 * (legacy links) still means "compose".
 */
export function parseLeadFocus({ hash = "", search = "" } = {}) {
  const h = String(hash || "").replace(/^#/, "");
  const params = new URLSearchParams(search || "");
  const composeTemplateId = params.get("compose_template") || null;
  let focus = null;
  if (h === LEAD_FOCUS_CONVERSATION) focus = LEAD_FOCUS_CONVERSATION;
  else if (h === LEAD_FOCUS_COMPOSE || composeTemplateId) focus = LEAD_FOCUS_COMPOSE;
  return { focus, composeTemplateId };
}

export function whatsappWindowOpen(lastInboundAt, now = Date.now()) {
  if (!lastInboundAt) return false;
  const t = Date.parse(lastInboundAt);
  return Number.isFinite(t) && now - t < WHATSAPP_WINDOW_MS;
}

/**
 * Which section LeadDetailPage reveals for a focus request.
 *
 *   conversation mounted (admin) + "conversation"        → conversation
 *   conversation mounted + "compose" + 24h window open   → conversation
 *     (the composer loads the recommended suggestion)
 *   "compose" with the window closed, or no conversation → the
 *     "Mensajes sugeridos" card (copy / wa.me / mark-sent flows), which
 *     also receives the template preselection
 */
export function resolveLeadFocusTarget({ focus, conversationMounted, windowOpen }) {
  if (!focus) return null;
  if (conversationMounted && (focus === LEAD_FOCUS_CONVERSATION || windowOpen)) {
    return LEAD_SECTION_CONVERSATION_ID;
  }
  return LEAD_SECTION_TEMPLATES_ID;
}
